import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Plus } from "@phosphor-icons/react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import AppointmentList from "@/components/app/AppointmentList";
import AppointmentDialog from "@/components/app/AppointmentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  client_id: "",
  title: "",
  date: "",
  time_start: "",
  time_end: "",
  appointment_type: "appuntamento",
  notes: "",
};

export default function CalendarPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const currentMonth = format(selectedDate, "yyyy-MM");
  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchAppointments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments?month=${currentMonth}`);
      setAppointments(data);
    } catch (err) {
      console.warn("[CalendarPage] fetchAppointments failed", err);
    }
  }, [currentMonth]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(data);
    } catch (err) {
      console.warn("[CalendarPage] fetchClients failed", err);
    }
  }, []);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  const dayAppointments = appointments.filter((a) => a.date === selectedDateStr);
  const appointmentDates = [...new Set(appointments.map((a) => a.date))];

  const handleSave = async () => {
    if (!form.title || !form.date) {
      toast.error("Titolo e data sono obbligatori");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/appointments/${editing.id}`, form);
        toast.success("Appuntamento aggiornato");
      } else {
        await axios.post(`${API}/appointments`, form);
        toast.success("Appuntamento creato");
      }
      setShowDialog(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchAppointments();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo appuntamento?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success("Appuntamento eliminato");
      fetchAppointments();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, date: selectedDateStr });
    setShowDialog(true);
  };

  const openEdit = (apt) => {
    setEditing(apt);
    setForm({
      client_id: apt.client_id || "",
      title: apt.title,
      date: apt.date,
      time_start: apt.time_start || "",
      time_end: apt.time_end || "",
      appointment_type: apt.appointment_type || "appuntamento",
      notes: apt.notes || "",
    });
    setShowDialog(true);
  };

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            CALENDARIO
          </h1>
          <p className="text-zinc-500 text-sm mt-1 tracking-wide">
            Gestisci appuntamenti e check-in
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
          data-testid="new-appointment-button"
        >
          <Plus size={18} className="mr-2" /> Nuovo Appuntamento
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-[#171717] border border-[#262626] rounded-lg p-4" data-testid="calendar-widget">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              locale={it}
              className="w-full"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4 w-full",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-semibold text-white",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-transparent p-0 text-zinc-400 hover:text-white border border-[#262626] rounded-md inline-flex items-center justify-center",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex justify-between",
                head_cell: "text-zinc-500 rounded-md w-9 font-medium text-[0.75rem] uppercase",
                row: "flex w-full mt-1 justify-between",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent",
                day: "h-9 w-9 p-0 font-normal text-zinc-300 hover:bg-[#262626] rounded-md inline-flex items-center justify-center transition-colors",
                day_selected: "bg-[#007AFF] text-white hover:bg-[#007AFF] focus:bg-[#007AFF]",
                day_today: "border border-[#007AFF] text-[#007AFF] font-semibold",
                day_outside: "text-zinc-700",
                day_disabled: "text-zinc-700 opacity-50",
                day_hidden: "invisible",
              }}
              modifiers={{
                hasAppointment: (date) =>
                  appointmentDates.includes(format(date, "yyyy-MM-dd")),
              }}
              modifiersClassNames={{
                hasAppointment:
                  "after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:bg-[#D4FF00] after:rounded-full",
              }}
              onMonthChange={(m) => setSelectedDate(m)}
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <AppointmentList
            selectedDate={selectedDate}
            appointments={dayAppointments}
            onNew={openNew}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <AppointmentDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        editing={editing}
        form={form}
        setForm={setForm}
        clients={clients}
        onSave={handleSave}
      />
    </div>
  );
}
